import React from 'react';
import { Plus } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useTramiteTareasSummary } from '../hooks/useTramiteTareasSummary';
import type { TareasCountSource } from '../types';

interface TareasProgressCellProps {
  tramite: TareasCountSource;
  clickable?: boolean;
  onClick?: (tramite: TareasCountSource) => void;
}

/**
 * Barra 0-100 del checklist de tareas de una escritura. Lee los counts que
 * ya vienen en el listado (`tareas_count` / `tareas_completadas_count`), sin
 * hacer un fetch propio — igual que `TramiteBudgetIndicators`.
 */
export const TareasProgressCell: React.FC<TareasProgressCellProps> = ({
  tramite,
  clickable = false,
  onClick,
}) => {
  const { total, completadas, porcentaje } = useTramiteTareasSummary(tramite);

  const handleClick = () => {
    if (clickable) {
      onClick?.(tramite);
    }
  };

  if (total === 0) {
    if (!clickable) {
      return <span className="text-xs text-muted-foreground">Sin tareas</span>;
    }

    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        onClick={handleClick}
        aria-label={`Agregar tareas a la escritura ${tramite.id}`}
      >
        <Plus className="h-3 w-3" />
        Agregar tareas
      </button>
    );
  }

  const content = (
    <div className="w-full min-w-[110px]">
      <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="font-mono font-medium">
          {completadas}/{total}
        </span>
        <span className="font-mono">{Math.round(porcentaje)}%</span>
      </div>
      <Progress value={porcentaje} className="h-1.5" />
    </div>
  );

  if (!clickable) {
    return content;
  }

  return (
    <button
      type="button"
      className="w-full rounded-md text-left transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={handleClick}
      aria-label={`Ver tareas de la escritura ${tramite.id}`}
    >
      {content}
    </button>
  );
};

export default TareasProgressCell;
